import type { PortalThread, PortalThreadDetail } from '@central/api';
import { Card, StatusChip, color, font, formatDateTime } from '@central/ui';
import { Alert, Button } from '@mantine/core';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { api, threadQuery } from '../api';
import { PeopleNotified } from '../components/PeopleNotified';
import { QueryState } from '../components/QueryState';
import { MergedBanner, RelatedList } from '../components/ThreadLinks';
import { customerVisible } from '../components/messageRules';
import { MessageItem, Reply } from '../components/ThreadMessages';
import {
  authorLabel,
  priorityLabel,
  statusLabel,
  statusTone,
  threadChip,
} from '../components/threadLabels';
import { errorMessage } from '../format';
import type { MessagesListSearch } from './messagesSearch';

const muted: CSSProperties = { color: color.textMuted };

/**
 * One conversation beside its details. The search carries the list it was
 * opened from, for the way back.
 */
export function Thread() {
  const { threadId } = useParams({ from: '/app/messages/$threadId' });
  const search = useSearch({ from: '/app/messages/$threadId' });
  return (
    <>
      <Breadcrumb id={threadId} search={search} />
      {/* Keyed so a draft never outlives its thread: a cached thread renders without a
          loading state, which would otherwise keep the same Reply mounted. */}
      <ThreadPane key={threadId} id={threadId} />
    </>
  );
}

/** The way back to the list, and the conversation's reference once it is loaded. */
function Breadcrumb({ id, search }: { id: string; search: MessagesListSearch }) {
  const detail = useInfiniteQuery(threadQuery(id));
  const ref = detail.data?.pages[0]?.thread.ref;
  return (
    <nav
      aria-label="Breadcrumb"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        minHeight: 40,
        marginBottom: 8,
      }}
    >
      <Link
        to="/messages"
        search={{ show: search.show, q: search.q }}
        // A way back, never the current page: the list is under every conversation's path.
        activeOptions={{ exact: true }}
        style={{
          color: color.link,
          textDecoration: 'none',
          minHeight: 40,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <span aria-hidden>←&nbsp;</span>
        Messages & reports
      </Link>
      {ref && (
        <>
          <span aria-hidden style={muted}>
            /
          </span>
          <span style={{ fontFamily: font.mono, fontWeight: 600 }}>{ref}</span>
        </>
      )}
    </nav>
  );
}

/** The thread, marked read on open, with earlier messages loaded on request. */
function ThreadPane({ id }: { id: string }) {
  const { data, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    threadQuery(id),
  );
  const qc = useQueryClient();
  const [earlierError, setEarlierError] = useState<unknown>();
  const loadEarlier = async () => {
    setEarlierError(undefined);
    const r = await fetchNextPage();
    if (r.isFetchNextPageError) setEarlierError(r.error);
  };
  const unread = data?.pages[0]?.thread.unread ?? 0;
  const { mutate: markRead } = useMutation({
    mutationFn: () => api.threads.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['threads'] }),
  });
  useEffect(() => {
    if (unread > 0) markRead();
  }, [unread, markRead]);

  if (!data) {
    return (
      <Card>
        <QueryState error={error} />
      </Card>
    );
  }
  return (
    <ThreadBody
      d={{
        ...data.pages[0]!,
        messages: data.pages.toReversed().flatMap((p) => p.messages),
      }}
      earlier={
        hasNextPage && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button
              variant="default"
              size="xs"
              style={{ alignSelf: 'center' }}
              loading={isFetchingNextPage}
              onClick={() => void loadEarlier()}
            >
              Load earlier messages
            </Button>
            {earlierError !== undefined && <Alert color="red">{errorMessage(earlierError)}</Alert>}
          </div>
        )
      }
    />
  );
}

function ThreadBody({ d, earlier }: { d: PortalThreadDetail; earlier: ReactNode }) {
  const t = d.thread;
  // A merged conversation is closed for good: its messages moved to another.
  const merged = !!d.mergedInto;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <ThreadHeader t={t} merged={merged} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
        <div
          style={{
            flex: '999 1 520px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <MergedBanner d={d} />
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {earlier}
              <ol
                aria-label="Messages"
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                {customerVisible(d.messages).map((m) => (
                  <MessageItem key={m.id} m={m} />
                ))}
              </ol>
              {!merged && <Reply threadId={t.id} resolved={t.status === 'closed'} />}
            </div>
          </Card>
        </div>
        <aside
          aria-label="Conversation details"
          style={{
            flex: '1 1 280px',
            maxWidth: 340,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <SideCard title="Details">
            <ThreadDetails t={t} />
          </SideCard>
          <SideCard title="History">
            <ThreadHistory t={t} />
          </SideCard>
          {(d.related ?? []).length > 0 && (
            <SideCard title="Related">
              <RelatedList d={d} />
            </SideCard>
          )}
          <SideCard title="People notified">
            <PeopleNotified threadId={t.id} />
          </SideCard>
        </aside>
      </div>
    </div>
  );
}

/** Kind and status, the subject, and the PDF and resolve actions. */
function ThreadHeader({ t, merged }: { t: PortalThread; merged: boolean }) {
  const chip = threadChip(t);
  return (
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
          <StatusChip tone={statusTone[t.status]}>{statusLabel[t.status]}</StatusChip>
        </div>
        <h1 style={{ margin: 0, fontSize: 24, lineHeight: '32px', overflowWrap: 'anywhere' }}>
          {t.subject}
        </h1>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Button component="a" href={api.threads.reportUrl(t.id)} download variant="default" h={40}>
          Download PDF
        </Button>
        {!merged && <ResolveButton t={t} />}
      </div>
    </header>
  );
}

/** Mark resolved on an open conversation, Reopen on a resolved one. */
function ResolveButton({ t }: { t: PortalThread }) {
  const qc = useQueryClient();
  const resolved = t.status === 'closed';
  const change = useMutation({
    mutationFn: () => (resolved ? api.threads.reopen(t.id) : api.threads.resolve(t.id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['threads'] }),
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
      <Button
        h={40}
        variant={resolved ? 'default' : 'filled'}
        loading={change.isPending}
        onClick={() => change.mutate()}
      >
        {resolved ? 'Reopen' : 'Mark resolved'}
      </Button>
      {change.error && <Alert color="red">{errorMessage(change.error)}</Alert>}
    </div>
  );
}

/** A sidebar card under a small uppercase heading. */
function SideCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card style={{ width: '100%' }}>
      <h2
        style={{
          margin: '0 0 12px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          ...muted,
        }}
      >
        {title}
      </h2>
      {children}
    </Card>
  );
}

const dlStyle: CSSProperties = {
  margin: 0,
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  columnGap: 12,
  rowGap: 8,
  fontSize: 13,
};

const ddStyle: CSSProperties = { margin: 0, minWidth: 0, overflowWrap: 'anywhere' };

function ThreadDetails({ t }: { t: PortalThread }) {
  return (
    <dl style={dlStyle}>
      <dt style={muted}>Reference</dt>
      <dd style={{ ...ddStyle, fontFamily: font.mono }}>{t.ref}</dd>
      <dt style={muted}>Status</dt>
      <dd style={ddStyle}>{statusLabel[t.status]}</dd>
      <dt style={muted}>Priority</dt>
      <dd style={ddStyle}>{priorityLabel[t.priority]}</dd>
      {t.environment && (
        <>
          <dt style={muted}>Deployment</dt>
          <dd style={ddStyle}>
            <Link to="/deployments/$deploymentId" params={{ deploymentId: t.environment.id }}>
              {t.environment.name}
            </Link>
          </dd>
        </>
      )}
      {t.area && (
        <>
          <dt style={muted}>Area</dt>
          <dd style={ddStyle}>{t.area}</dd>
        </>
      )}
      {t.crashCount !== undefined && (
        <>
          <dt style={muted}>Crashes</dt>
          <dd style={ddStyle}>{t.crashCount}</dd>
        </>
      )}
    </dl>
  );
}

/** Who reported the conversation and when, and who resolved it and when. */
function ThreadHistory({ t }: { t: PortalThread }) {
  return (
    <dl style={dlStyle}>
      <dt style={muted}>Reported</dt>
      <dd style={ddStyle}>
        {formatDateTime(t.createdAt)} by {authorLabel(t.reportedBy)}
      </dd>
      {t.resolvedAt && t.resolvedBy && (
        <>
          <dt style={muted}>Resolved</dt>
          <dd style={ddStyle}>
            {formatDateTime(t.resolvedAt)} by {authorLabel(t.resolvedBy)}
          </dd>
        </>
      )}
    </dl>
  );
}
